// AI Training page data
export const aiTrainingData = {
  // Hero section
  hero: {
    title: 'AI Training & Education Programs',
    subtitle:
      'Empower your team with practical AI skills through our specialized training programs',
    imageUrl: '/assets/images/services/ai-training-hero.jpg',
  },

  // Page metadata
  title: 'AI Training & Education Programs',
  subtitle: 'Empower your team with practical AI skills through our specialized training programs',
  description:
    'Comprehensive training programs designed to upskill your team on the latest AI technologies and best practices.',
  overview:
    "Our AI Training & Education programs provide comprehensive, hands-on learning experiences tailored to your team's existing skill levels and your organization's specific goals. Whether you need to build foundational AI literacy across your organization or develop specialized technical capabilities in key roles, our training solutions combine theoretical knowledge with practical application to ensure your team can effectively leverage AI technologies.",
  serviceName: 'AI Training & Education',
  nextService: {
    name: 'Business AI Onboarding',
    url: '/services/ai-onboarding',
  },
  relatedCaseStudies: ['case-study-1', 'case-study-3'],

  // Key benefits section
  keyBenefits: [
    {
      title: 'Increased Productivity',
      description:
        'Automate repetitive tasks and streamline workflows, allowing your team to focus on higher-value activities.',
      icon: 'productivity',
    },
    {
      title: 'Custom Learning Paths',
      description:
        'Training programs tailored to individual roles and experience levels for maximum relevance.',
      icon: 'path',
    },
    {
      title: 'Real-World Applications',
      description:
        'Practical, hands-on exercises based on real business scenarios in your industry.',
      icon: 'application',
    },
    {
      title: 'Reduced Implementation Time',
      description: 'Faster adoption of AI tools and methodologies with properly trained staff.',
      icon: 'time',
    },
    {
      title: 'Competitive Advantage',
      description:
        'Gain an edge in your market by developing AI capabilities ahead of industry peers.',
      icon: 'advantage',
    },
    {
      title: 'Continuous Support',
      description: 'Ongoing access to AI experts for questions and guidance post-training.',
      icon: 'support',
    },
  ],

  // FAQs
  faqs: [
    {
      question: 'What types of AI training do you offer?',
      answer:
        'We offer a range of training programs, from introductory AI literacy courses for all employees to specialized technical training for data scientists and developers. Our programs include AI Fundamentals Workshops, Role-Based Skills Programs, Implementation Certification, and Custom AI Solutions Development training.',
    },
    {
      question: 'How long does training typically take?',
      answer:
        "Training duration varies based on the program and your team's existing knowledge. Our workshops can be completed in a single day, while more comprehensive programs may span several weeks. We work with you to create a schedule that minimizes disruption to your operations.",
    },
    {
      question: 'Can you customize training for our specific industry?',
      answer:
        'Absolutely. All our training programs are tailored to your industry, business processes, and specific use cases. We incorporate relevant examples, exercises, and projects that directly relate to the challenges and opportunities in your sector.',
    },
    {
      question: 'Do we need technical expertise before starting training?',
      answer:
        "No prior technical expertise is required for our foundational programs. We design our training to match your team's current skill levels, whether they're complete beginners or experienced technologists looking to expand their AI capabilities.",
    },
    {
      question: 'How do you measure training effectiveness?',
      answer:
        'We establish clear learning objectives at the outset and use a combination of assessments, project outcomes, and post-training surveys to measure effectiveness. For many clients, we also help establish KPIs to track how the training impacts actual business performance.',
    },
  ],

  // Training programs section
  programs: [
    {
      title: 'AI Fundamentals Workshop',
      description:
        'A one-day intensive workshop introducing key AI concepts, applications, and implementation considerations for decision-makers and leadership teams.',
      features: [
        'Overview of AI technologies and capabilities',
        'Business use case identification',
        'Implementation strategy fundamentals',
        'ROI calculation frameworks',
        'Risk assessment and management',
      ],
      forWhom: 'Executives, Directors, Managers',
      duration: '1 day',
      format: 'In-person or virtual, interactive workshop',
    },
    {
      title: 'Role-Based AI Skills Program',
      description:
        'Targeted training series focusing on AI applications and skills specific to different business functions and professional roles.',
      features: [
        'Function-specific AI applications',
        'Hands-on tool training',
        'Process optimization tactics',
        'Integration with existing workflows',
        'Practical implementation exercises',
      ],
      forWhom: 'Department teams, Specialists',
      duration: '3-5 days',
      format: 'Modular training with hands-on labs',
    },
    {
      title: 'AI Implementation Certification',
      description:
        'Comprehensive program covering the entire AI implementation lifecycle, from planning to deployment and optimization.',
      features: [
        'Project management methodology',
        'Technical integration planning',
        'Change management strategies',
        'Testing and validation methods',
        'Performance monitoring and improvement',
      ],
      forWhom: 'IT Teams, Project Managers, Implementation Leads',
      duration: '2-4 weeks',
      format: 'Blended learning with capstone project',
    },
    {
      title: 'Custom AI Solutions Development',
      description:
        'Advanced training for technical teams on developing and customizing AI solutions for specific business requirements.',
      features: [
        'AI framework fundamentals',
        'Model selection and tuning',
        'Data preparation and management',
        'Solution architecture design',
        'Deployment and scaling strategies',
      ],
      forWhom: 'Developers, Data Scientists, Engineers',
      duration: '4-8 weeks',
      format: 'Intensive bootcamp with mentored project work',
    },
  ],

  // Implementation process
  implementationProcess: [
    {
      step: '1',
      title: 'Assessment',
      description:
        "We evaluate your organization's current AI capabilities, skill gaps, and learning objectives to create a tailored training plan.",
      icon: 'assessment',
    },
    {
      step: '2',
      title: 'Customization',
      description:
        'Training materials and exercises are adapted to your industry, specific business processes, and team composition.',
      icon: 'customization',
    },
    {
      step: '3',
      title: 'Delivery',
      description:
        'Expert instructors deliver training through your preferred format: on-site, virtual, self-paced, or blended approaches.',
      icon: 'delivery',
    },
    {
      step: '4',
      title: 'Practical Application',
      description:
        'Participants work on real business challenges using AI tools and techniques relevant to their roles.',
      icon: 'application',
    },
    {
      step: '5',
      title: 'Evaluation',
      description:
        'Skills assessment and feedback sessions ensure learning objectives are met and identify areas for further development.',
      icon: 'evaluation',
    },
    {
      step: '6',
      title: 'Ongoing Support',
      description:
        'Post-training resources, office hours, and mentorship opportunities to reinforce learning and address questions.',
      icon: 'support',
    },
  ],

  // Pricing tiers
  pricingTiers: [
    {
      name: 'Essentials',
      description: 'Perfect for small teams or organizations just beginning their AI journey',
      price: '$1,500',
      perUnit: 'per person',
      features: [
        'AI Fundamentals Workshop',
        'Basic tool training',
        'Digital learning resources',
        '30 days email support',
        'Completion certificate',
      ],
      isPopular: false,
      ctaText: 'Get Started',
    },
    {
      name: 'Professional',
      description: 'Comprehensive training for organizations serious about AI adoption',
      price: '$3,500',
      perUnit: 'per person',
      features: [
        'All Essentials features',
        'Role-Based AI Skills Program',
        'Hands-on project work',
        '6 months expert support',
        'Implementation planning session',
        'AI readiness assessment',
      ],
      isPopular: true,
      ctaText: 'Most Popular',
    },
    {
      name: 'Enterprise',
      description: 'Custom training solutions for large organizations with complex needs',
      price: 'Custom',
      perUnit: '',
      features: [
        'All Professional features',
        'AI Implementation Certification',
        'Custom AI Solutions Development',
        'Executive briefing sessions',
        'On-site training options',
        'Dedicated training manager',
        'Unlimited support (12 months)',
      ],
      isPopular: false,
      ctaText: 'Contact Sales',
    },
  ],
};

// AI Onboarding page data
export const aiOnboardingData = {
  // Add similar structure as above but for AI Onboarding
};

// Workflow Automation page data
export const workflowAutomationData = {
  // Add similar structure as above but for Workflow Automation
};

// Custom AI Development page data
export const customAIDevelopmentData = {
  // Add similar structure as above but for Custom AI Development
};

// AI System Optimization page data
export const aiOptimizationData = {
  // Add similar structure as above but for AI System Optimization
};

// Web Design & Development page data
export const webDesignData = {
  // Hero section
  hero: {
    title: 'AI-Enhanced Web Design & Development',
    subtitle:
      'Create stunning, conversion-optimized websites that adapt and improve with intelligent user experience optimization',
    imageUrl: '/assets/images/services/web-design-hero.jpg',
  },

  // Page metadata
  title: 'AI-Enhanced Web Design & Development',
  subtitle:
    'Create stunning, conversion-optimized websites that adapt and improve with intelligent user experience optimization',
  description:
    'Professional web design and development services enhanced with AI-powered optimization, personalization, and performance monitoring.',
  overview:
    'Our AI-Enhanced Web Design & Development service combines creative excellence with intelligent technology to create websites that not only look stunning but continuously optimize themselves for better performance. We leverage artificial intelligence for user behavior analysis, A/B testing automation, personalization engines, and conversion rate optimization to ensure your website delivers exceptional results from day one and improves over time.',
  serviceName: 'Web Design & Development',
  nextService: {
    name: 'Custom Software Development',
    url: '/services/software-development',
  },
  relatedCaseStudies: ['case-study-1', 'case-study-2'],

  // Key benefits section
  keyBenefits: [
    {
      title: 'AI-Powered Conversion Optimization',
      description:
        'Intelligent systems that automatically test and optimize page elements to maximize conversions and user engagement.',
      icon: 'optimization',
    },
    {
      title: 'Responsive & Mobile-First Design',
      description:
        'Beautiful, fast-loading websites that provide exceptional experiences across all devices and screen sizes.',
      icon: 'responsive',
    },
    {
      title: 'Smart Personalization Engine',
      description:
        'AI-driven content and layout personalization that adapts to individual user preferences and behaviors.',
      icon: 'personalization',
    },
    {
      title: 'Performance & SEO Optimized',
      description:
        'Built-in technical SEO optimization and performance monitoring for superior search rankings and user experience.',
      icon: 'performance',
    },
    {
      title: 'Scalable & Future-Ready',
      description:
        'Modern architecture and clean code that grows with your business and adapts to new technologies.',
      icon: 'scalable',
    },
    {
      title: 'Ongoing AI Insights',
      description:
        'Continuous monitoring and intelligent recommendations for website improvements and optimization opportunities.',
      icon: 'insights',
    },
  ],

  // FAQs
  faqs: [
    {
      question: 'How does AI enhance traditional web design?',
      answer:
        'Our AI enhancement includes automated A/B testing, user behavior analysis, personalized content delivery, conversion optimization, and performance monitoring. This means your website continuously improves itself based on real user data and behavior patterns.',
    },
    {
      question: "What's included in the web design process?",
      answer:
        'Our process includes strategy consultation, UX/UI design, responsive development, AI integration, content optimization, SEO setup, performance optimization, testing, launch, and 30 days of post-launch support and monitoring.',
    },
    {
      question: 'How long does a typical web design project take?',
      answer:
        'Project timelines vary based on complexity. A standard business website takes 4-6 weeks, while more complex web applications can take 8-12 weeks. We provide detailed timelines during the planning phase.',
    },
    {
      question: 'Do you provide ongoing maintenance and updates?',
      answer:
        'Yes, we offer comprehensive maintenance packages that include security updates, performance monitoring, content updates, AI optimization reports, and technical support to keep your website running smoothly.',
    },
    {
      question: 'Can you redesign our existing website?',
      answer:
        'Absolutely! We specialize in website redesigns and can enhance your existing site with AI capabilities, improved design, better performance, and conversion optimization while preserving your brand identity.',
    },
    {
      question: 'What platforms and technologies do you use?',
      answer:
        'We use modern technologies including React, Next.js, WordPress, Shopify, and custom solutions. Our AI integrations work with any platform and can be customized to your specific needs and existing systems.',
    },
  ],

  // Service features/process
  features: [
    {
      title: 'Strategy & Planning',
      description:
        'Comprehensive analysis of your business goals, target audience, and competitive landscape to create a strategic foundation for your website.',
      features: [
        'Business goal analysis and KPI definition',
        'Target audience research and persona development',
        'Competitive analysis and market positioning',
        'Technical requirements and platform selection',
        'AI integration planning and optimization strategy',
      ],
      forWhom: 'Business Owners, Marketing Teams',
      duration: '1-2 weeks',
      format: 'Collaborative workshops and research',
    },
    {
      title: 'Design & User Experience',
      description:
        'Creative design process that combines aesthetic excellence with user experience best practices and AI-driven optimization insights.',
      features: [
        'Custom visual design and brand integration',
        'User experience optimization and wireframing',
        'Responsive design for all devices',
        'Accessibility compliance and inclusive design',
        'AI-powered personalization framework',
      ],
      forWhom: 'Design Teams, Stakeholders',
      duration: '2-3 weeks',
      format: 'Design sprints with feedback cycles',
    },
    {
      title: 'Development & Integration',
      description:
        'Professional development using modern technologies with AI capabilities built-in for optimal performance and scalability.',
      features: [
        'Clean, maintainable code development',
        'AI integration and optimization tools',
        'Content management system setup',
        'Third-party integrations and APIs',
        'Security implementation and testing',
      ],
      forWhom: 'Technical Teams, Developers',
      duration: '3-4 weeks',
      format: 'Agile development with regular updates',
    },
    {
      title: 'Launch & Optimization',
      description:
        'Comprehensive testing, launch preparation, and ongoing AI-powered optimization to ensure maximum performance and results.',
      features: [
        'Quality assurance and cross-browser testing',
        'Performance optimization and speed testing',
        'SEO setup and search engine submission',
        'AI monitoring and analytics implementation',
        'Launch support and post-launch optimization',
      ],
      forWhom: 'All Stakeholders',
      duration: '1-2 weeks',
      format: 'Testing, launch, and monitoring',
    },
  ],

  // Implementation process
  implementationProcess: [
    {
      step: '1',
      title: 'Discovery & Strategy',
      description:
        'We analyze your business goals, target audience, and technical requirements to create a comprehensive project roadmap.',
      icon: 'discovery',
    },
    {
      step: '2',
      title: 'Design & Prototyping',
      description:
        'Our design team creates wireframes, mockups, and interactive prototypes that align with your brand and user needs.',
      icon: 'design',
    },
    {
      step: '3',
      title: 'Development & AI Integration',
      description:
        'We build your website using modern technologies and integrate AI capabilities for optimization and personalization.',
      icon: 'development',
    },
    {
      step: '4',
      title: 'Testing & Quality Assurance',
      description:
        'Comprehensive testing across devices, browsers, and performance metrics to ensure flawless functionality.',
      icon: 'testing',
    },
    {
      step: '5',
      title: 'Launch & Deployment',
      description:
        'We handle the complete launch process, including domain setup, hosting, and go-live coordination.',
      icon: 'launch',
    },
    {
      step: '6',
      title: 'Optimization & Support',
      description:
        "Ongoing AI-powered optimization, performance monitoring, and support to maximize your website's effectiveness.",
      icon: 'optimization',
    },
  ],

  // Pricing tiers
  pricingTiers: [
    {
      name: 'Starter',
      description: 'Perfect for small businesses and startups needing a professional web presence',
      price: '$2,500',
      perUnit: 'one-time',
      features: [
        'Up to 5 pages responsive website',
        'Mobile-optimized design',
        'Basic AI analytics integration',
        'Contact forms and basic functionality',
        'SEO optimization setup',
        '30 days post-launch support',
      ],
      isPopular: false,
      ctaText: 'Get Started',
    },
    {
      name: 'Professional',
      description: 'Comprehensive solution for growing businesses with advanced AI features',
      price: '$7,500',
      perUnit: 'one-time',
      features: [
        'Up to 15 pages with custom design',
        'Advanced AI personalization engine',
        'E-commerce or booking functionality',
        'Custom integrations and APIs',
        'Advanced SEO and performance optimization',
        '90 days support and optimization',
      ],
      isPopular: true,
      ctaText: 'Most Popular',
    },
    {
      name: 'Enterprise',
      description: 'Custom web applications with full AI integration for large organizations',
      price: 'Custom',
      perUnit: '',
      features: [
        'Unlimited pages and custom functionality',
        'Full AI optimization suite',
        'Custom web application development',
        'Advanced integrations and automation',
        'Dedicated project manager',
        '12 months support and optimization',
      ],
      isPopular: false,
      ctaText: 'Contact Sales',
    },
  ],
};

// Custom Software Development page data
export const softwareDevelopmentData = {
  // Hero section
  hero: {
    title: 'Custom Software Development with AI Capabilities',
    subtitle:
      'Bespoke software solutions designed to scale with your business and integrate seamlessly with intelligent automation',
    imageUrl: '/assets/images/services/software-development-hero.jpg',
  },

  // Page metadata
  title: 'Custom Software Development with AI Capabilities',
  subtitle:
    'Bespoke software solutions designed to scale with your business and integrate seamlessly with intelligent automation',
  description:
    'Custom software development services with AI integration, built to scale with your business and deliver exceptional performance.',
  overview:
    'Our Custom Software Development service delivers tailor-made applications that incorporate AI capabilities from the ground up. We build scalable, maintainable software solutions that integrate seamlessly with your existing systems while leveraging artificial intelligence to automate processes, enhance decision-making, and provide competitive advantages.',
  serviceName: 'Custom Software Development',
  nextService: {
    name: 'SEO Services',
    url: '/services/seo',
  },
  relatedCaseStudies: ['case-study-2', 'case-study-3'],

  // Key benefits section
  keyBenefits: [
    {
      title: 'AI-Integrated Architecture',
      description:
        'Software built with AI capabilities at the core, enabling intelligent automation and data-driven decision making.',
      icon: 'ai-integration',
    },
    {
      title: 'Scalable & Maintainable Code',
      description:
        'Modern development practices ensure your software grows with your business and remains easy to maintain.',
      icon: 'scalable',
    },
    {
      title: 'Seamless Integration',
      description:
        'Custom APIs and integrations that connect your new software with existing business systems and workflows.',
      icon: 'integration',
    },
    {
      title: 'Performance Optimization',
      description:
        'High-performance applications that handle increasing loads and deliver fast, responsive user experiences.',
      icon: 'performance',
    },
    {
      title: 'Security & Compliance',
      description:
        'Enterprise-grade security measures and compliance with industry standards and regulations.',
      icon: 'security',
    },
    {
      title: 'Ongoing Support & Evolution',
      description:
        'Continuous monitoring, updates, and feature enhancements to keep your software current and effective.',
      icon: 'support',
    },
  ],

  // FAQs
  faqs: [
    {
      question: 'What types of custom software do you develop?',
      answer:
        'We develop web applications, mobile apps, desktop software, APIs, integrations, and specialized business applications. Our AI integration can enhance any software type with intelligent automation, predictive analytics, and smart decision-making capabilities.',
    },
    {
      question: 'How do you ensure the software scales with our business?',
      answer:
        'We use microservices architecture, cloud-native technologies, and scalable database designs. Our development follows best practices for horizontal scaling, load balancing, and performance optimization to ensure your software grows seamlessly with your business needs.',
    },
    {
      question: 'Can you integrate AI into existing software systems?',
      answer:
        'Absolutely! We specialize in AI integration with legacy systems. Our approach includes API development, data pipeline creation, and gradual AI enhancement to minimize disruption while maximizing the benefits of intelligent automation.',
    },
    {
      question: 'What is your development process?',
      answer:
        'We follow agile methodologies with sprints, regular demos, and continuous feedback. The process includes discovery, design, development, testing, deployment, and ongoing support. We provide transparent progress updates and involve you at every stage.',
    },
    {
      question: 'How do you handle data security and privacy?',
      answer:
        'Security is built into every aspect of our development process. We implement encryption, secure authentication, regular security audits, compliance with GDPR/CCPA, and follow industry best practices for data protection and privacy.',
    },
    {
      question: 'What technologies and platforms do you use?',
      answer:
        'We work with modern technologies including React, Node.js, Python, .NET, cloud platforms (AWS, Azure, GCP), databases (PostgreSQL, MongoDB), and AI frameworks (TensorFlow, PyTorch). We select the best technology stack for your specific requirements.',
    },
  ],

  // Service features/process
  features: [
    {
      title: 'Requirements Analysis & Planning',
      description:
        'Comprehensive analysis of your business needs, technical requirements, and AI integration opportunities.',
      features: [
        'Business process analysis and workflow mapping',
        'Technical requirements gathering and specification',
        'AI capability assessment and integration planning',
        'Architecture design and technology selection',
        'Project roadmap and timeline development',
      ],
      forWhom: 'Business Stakeholders, IT Teams',
      duration: '2-4 weeks',
      format: 'Workshops and collaborative planning sessions',
    },
    {
      title: 'Design & Prototyping',
      description:
        'User experience design and technical architecture that incorporates AI capabilities from the foundation.',
      features: [
        'UI/UX design and user journey mapping',
        'Technical architecture and system design',
        'AI integration design and data flow planning',
        'API design and integration specifications',
        'Prototype development and validation',
      ],
      forWhom: 'Design Teams, Architects, Developers',
      duration: '3-6 weeks',
      format: 'Design sprints with iterative feedback',
    },
    {
      title: 'Development & AI Integration',
      description:
        'Agile development with continuous AI integration, testing, and quality assurance throughout the process.',
      features: [
        'Clean, scalable code development',
        'AI model integration and training',
        'Database design and optimization',
        'API development and third-party integrations',
        'Automated testing and quality assurance',
      ],
      forWhom: 'Development Teams, QA Teams',
      duration: '8-16 weeks',
      format: 'Agile sprints with bi-weekly demos',
    },
    {
      title: 'Deployment & Optimization',
      description:
        'Secure deployment, performance optimization, and continuous monitoring with AI-powered insights.',
      features: [
        'Production deployment and scaling setup',
        'Performance monitoring and optimization',
        'AI model fine-tuning and performance tracking',
        'Security hardening and compliance validation',
        'User training and documentation',
      ],
      forWhom: 'DevOps Teams, Operations Teams',
      duration: '2-4 weeks',
      format: 'Staged deployment with monitoring',
    },
  ],

  // Implementation process
  implementationProcess: [
    {
      step: '1',
      title: 'Discovery & Analysis',
      description:
        'We analyze your business requirements, technical needs, and AI integration opportunities to create a comprehensive development plan.',
      icon: 'discovery',
    },
    {
      step: '2',
      title: 'Architecture & Design',
      description:
        'Our team designs scalable architecture and user experiences that incorporate AI capabilities from the ground up.',
      icon: 'design',
    },
    {
      step: '3',
      title: 'Development & Integration',
      description:
        'Agile development process with continuous integration of AI capabilities and regular stakeholder feedback.',
      icon: 'development',
    },
    {
      step: '4',
      title: 'Testing & Quality Assurance',
      description:
        'Comprehensive testing across functionality, performance, security, and AI model accuracy.',
      icon: 'testing',
    },
    {
      step: '5',
      title: 'Deployment & Training',
      description:
        'Secure production deployment with user training, documentation, and go-live support.',
      icon: 'deployment',
    },
    {
      step: '6',
      title: 'Monitoring & Optimization',
      description:
        'Continuous monitoring, performance optimization, and AI model refinement for ongoing improvement.',
      icon: 'optimization',
    },
  ],

  // Pricing tiers
  pricingTiers: [
    {
      name: 'MVP Development',
      description: 'Minimum viable product development for startups and new business initiatives',
      price: '$15,000',
      perUnit: 'one-time',
      features: [
        'Core feature set development',
        'Basic AI integration',
        'Responsive web or mobile app',
        'User authentication and basic security',
        '3 months post-launch support',
        'Basic documentation and training',
      ],
      isPopular: false,
      ctaText: 'Build MVP',
    },
    {
      name: 'Professional',
      description: 'Full-featured application development with advanced AI capabilities',
      price: '$45,000',
      perUnit: 'one-time',
      features: [
        'Complete feature development',
        'Advanced AI integrations',
        'Multi-platform deployment',
        'Advanced security and compliance',
        '6 months support and maintenance',
        'Comprehensive documentation',
        'Team training and onboarding',
      ],
      isPopular: true,
      ctaText: 'Most Popular',
    },
    {
      name: 'Enterprise',
      description: 'Large-scale software solutions with enterprise-grade AI and integrations',
      price: 'Custom',
      perUnit: '',
      features: [
        'Unlimited features and complexity',
        'Full AI ecosystem integration',
        'Enterprise architecture and scalability',
        'Advanced security and compliance',
        '12 months premium support',
        'Dedicated development team',
        'Custom integrations and APIs',
      ],
      isPopular: false,
      ctaText: 'Contact Sales',
    },
  ],
};

// SEO Services page data
export const seoData = {
  // Hero section
  hero: {
    title: 'AI-Driven SEO Services',
    subtitle:
      'Improve rankings, drive organic traffic, and increase conversions with intelligent search engine optimization',
    imageUrl: '/assets/images/services/seo-hero.jpg',
  },

  // Page metadata
  title: 'AI-Driven SEO Services',
  subtitle:
    'Improve rankings, drive organic traffic, and increase conversions with intelligent search engine optimization',
  description:
    'AI-powered search engine optimization services that improve rankings, drive organic traffic, and increase conversions.',
  overview:
    'Our AI-Driven SEO Services leverage artificial intelligence to optimize your online presence more effectively than traditional SEO methods. We use machine learning algorithms to analyze search patterns, predict algorithm changes, and continuously optimize your content and technical SEO for maximum visibility and conversions.',
  serviceName: 'SEO Services',
  nextService: {
    name: 'Digital Marketing',
    url: '/services/digital-marketing',
  },
  relatedCaseStudies: ['case-study-1', 'case-study-3'],

  // Key benefits section
  keyBenefits: [
    {
      title: 'AI-Powered Keyword Research',
      description:
        'Intelligent keyword discovery that identifies high-value opportunities and predicts search trends before they emerge.',
      icon: 'keyword-research',
    },
    {
      title: 'Predictive Algorithm Analysis',
      description:
        'Machine learning models that anticipate search algorithm changes and adapt your strategy proactively.',
      icon: 'predictive-analysis',
    },
    {
      title: 'Automated Content Optimization',
      description:
        'AI-driven content analysis and optimization that improves readability, relevance, and search performance.',
      icon: 'content-optimization',
    },
    {
      title: 'Technical SEO Automation',
      description:
        'Automated technical SEO audits, fixes, and monitoring that ensure optimal site performance and crawlability.',
      icon: 'technical-seo',
    },
    {
      title: 'Competitor Intelligence',
      description:
        'AI-powered competitor analysis that identifies opportunities and strategies for gaining competitive advantage.',
      icon: 'competitor-intelligence',
    },
    {
      title: 'Performance Tracking & Insights',
      description:
        'Advanced analytics and AI-driven insights that measure ROI and identify optimization opportunities.',
      icon: 'performance-tracking',
    },
  ],

  // FAQs
  faqs: [
    {
      question: 'How does AI improve traditional SEO services?',
      answer:
        'AI enhances SEO by automating repetitive tasks, predicting search trends, analyzing vast amounts of data, and continuously optimizing strategies. Our AI systems can process millions of data points to identify patterns that humans might miss, leading to more effective and efficient SEO campaigns.',
    },
    {
      question: 'What types of SEO services do you provide?',
      answer:
        'We offer comprehensive SEO services including technical SEO, on-page optimization, off-page link building, local SEO, content strategy, and performance monitoring. All services are enhanced with AI capabilities for superior results.',
    },
    {
      question: 'How long does it take to see SEO results?',
      answer:
        'SEO is a long-term strategy. Most clients see initial improvements within 3-6 months, with significant results typically appearing after 6-12 months. Our AI systems help accelerate this process by identifying and prioritizing high-impact opportunities.',
    },
    {
      question: 'Do you guarantee first-page rankings?',
      answer:
        "While we can't guarantee specific rankings (no ethical SEO company can), we guarantee measurable improvements in organic traffic, keyword rankings, and conversion rates. Our AI-driven approach has helped clients achieve an average of 300% increase in organic traffic within 12 months.",
    },
    {
      question: 'Can you help with local SEO for my business?',
      answer:
        'Absolutely! Our AI-powered local SEO services optimize Google My Business profiles, local schema markup, review management, and local citation building. We use location-based algorithms to target customers in your geographic area effectively.',
    },
    {
      question: 'How do you measure SEO success?',
      answer:
        'We track over 50 KPIs including organic traffic growth, keyword ranking improvements, conversion rate optimization, domain authority increases, and ROI metrics. Our AI systems provide predictive analytics to forecast future performance and identify optimization opportunities.',
    },
  ],

  // Service features/process
  features: [
    {
      title: 'SEO Audit & Analysis',
      description:
        'Comprehensive AI-powered audit of your current SEO status and identification of optimization opportunities.',
      features: [
        'Technical SEO assessment and fixes',
        'Content quality and optimization analysis',
        'Backlink profile evaluation and strategy',
        'Competitor analysis and benchmarking',
        'Keyword opportunity identification',
        'Site speed and mobile optimization',
      ],
      forWhom: 'Website Owners, Marketing Teams',
      duration: '1-2 weeks',
      format: 'Comprehensive audit report and strategy session',
    },
    {
      title: 'Content Strategy & Optimization',
      description:
        'AI-driven content creation and optimization that improves search rankings and user engagement.',
      features: [
        'Keyword research and content gap analysis',
        'Content optimization and performance tracking',
        'Topic cluster development and internal linking',
        'User intent analysis and content alignment',
        'Content performance monitoring and refinement',
        'Multilingual SEO content strategy',
      ],
      forWhom: 'Content Teams, Marketing Teams',
      duration: 'Ongoing',
      format: 'Monthly content optimization and strategy updates',
    },
    {
      title: 'Technical SEO Implementation',
      description:
        'Automated technical SEO fixes and optimizations that improve site performance and search visibility.',
      features: [
        'Site speed optimization and Core Web Vitals',
        'Mobile-first indexing and responsive design',
        'Schema markup implementation and validation',
        'Internal linking structure optimization',
        'XML sitemap and robots.txt optimization',
        'International SEO setup and hreflang tags',
      ],
      forWhom: 'Technical Teams, Developers',
      duration: '2-4 weeks initial',
      format: 'Technical implementation with monitoring',
    },
    {
      title: 'Link Building & Authority',
      description:
        'Strategic link building campaigns that improve domain authority and search rankings.',
      features: [
        'High-quality backlink acquisition',
        'Content marketing and link-worthy asset creation',
        'Outreach campaign management',
        'Link profile monitoring and disavowal',
        'Local citation building and management',
        'Brand mention tracking and conversion',
      ],
      forWhom: 'Marketing Teams, PR Teams',
      duration: 'Ongoing',
      format: 'Monthly link building campaigns and reporting',
    },
  ],

  // Implementation process
  implementationProcess: [
    {
      step: '1',
      title: 'SEO Audit & Strategy',
      description:
        'Comprehensive analysis of your current SEO status and development of a customized optimization strategy.',
      icon: 'audit',
    },
    {
      step: '2',
      title: 'Technical Optimization',
      description:
        'Implementation of technical SEO fixes and optimizations to improve site performance and crawlability.',
      icon: 'technical',
    },
    {
      step: '3',
      title: 'Content Optimization',
      description:
        'AI-driven content optimization and creation to improve search rankings and user engagement.',
      icon: 'content',
    },
    {
      step: '4',
      title: 'Link Building Campaign',
      description:
        'Strategic link building and authority building to improve domain strength and search visibility.',
      icon: 'links',
    },
    {
      step: '5',
      title: 'Monitoring & Reporting',
      description:
        'Continuous monitoring of SEO performance with detailed reporting and optimization recommendations.',
      icon: 'monitoring',
    },
    {
      step: '6',
      title: 'Ongoing Optimization',
      description:
        'Continuous AI-powered optimization and strategy refinement based on performance data and algorithm changes.',
      icon: 'optimization',
    },
  ],

  // Pricing tiers
  pricingTiers: [
    {
      name: 'Local SEO',
      description: 'Essential SEO for local businesses wanting to dominate their geographic market',
      price: '$1,500',
      perUnit: 'per month',
      features: [
        'Local keyword optimization',
        'Google My Business optimization',
        'Local citation building and management',
        'Review management and monitoring',
        'Local schema markup implementation',
        'Monthly performance reporting',
      ],
      isPopular: false,
      ctaText: 'Dominate Locally',
    },
    {
      name: 'Professional SEO',
      description: 'Comprehensive SEO strategy for businesses serious about organic growth',
      price: '$3,500',
      perUnit: 'per month',
      features: [
        'Advanced keyword research and targeting',
        'Technical SEO audit and fixes',
        'Content optimization and creation',
        'Link building campaign management',
        'Competitor analysis and strategy',
        'Bi-weekly performance reports and strategy calls',
      ],
      isPopular: true,
      ctaText: 'Most Popular',
    },
    {
      name: 'Enterprise SEO',
      description: 'Advanced SEO solutions for large organizations with complex needs',
      price: '$7,500',
      perUnit: 'per month',
      features: [
        'Enterprise-level keyword strategy',
        'Advanced technical SEO implementation',
        'Large-scale content optimization',
        'Premium link building campaigns',
        'International SEO management',
        'Dedicated SEO strategist and weekly strategy sessions',
      ],
      isPopular: false,
      ctaText: 'Scale Globally',
    },
  ],
};

// Digital Marketing page data
export const digitalMarketingData = {
  // Hero section
  hero: {
    title: 'Data-Driven Digital Marketing with AI Insights',
    subtitle:
      'Maximize ROI and sustainable business growth through intelligent marketing campaigns and predictive analytics',
    imageUrl: '/assets/images/services/digital-marketing-hero.jpg',
  },

  // Page metadata
  title: 'Data-Driven Digital Marketing with AI Insights',
  subtitle:
    'Maximize ROI and sustainable business growth through intelligent marketing campaigns and predictive analytics',
  description:
    'AI-powered digital marketing services that maximize ROI and drive sustainable business growth through data-driven insights.',
  overview:
    'Our Data-Driven Digital Marketing service combines traditional marketing expertise with artificial intelligence to create campaigns that not only reach your target audience but continuously optimize themselves for maximum ROI. We use predictive analytics, automated A/B testing, and intelligent audience targeting to ensure your marketing budget delivers exceptional results.',
  serviceName: 'Digital Marketing',
  nextService: {
    name: 'AI Training & Education',
    url: '/services/ai-training',
  },
  relatedCaseStudies: ['case-study-1', 'case-study-2'],

  // Key benefits section
  keyBenefits: [
    {
      title: 'AI-Powered Audience Targeting',
      description:
        'Intelligent audience segmentation and targeting that identifies high-value prospects with precision.',
      icon: 'audience-targeting',
    },
    {
      title: 'Predictive Campaign Optimization',
      description:
        'Machine learning algorithms that predict campaign performance and automatically optimize for maximum ROI.',
      icon: 'predictive-optimization',
    },
    {
      title: 'Automated A/B Testing',
      description:
        'Continuous automated testing and optimization of ad creatives, landing pages, and campaign elements.',
      icon: 'ab-testing',
    },
    {
      title: 'Multi-Channel Campaign Management',
      description:
        'Coordinated campaigns across all digital channels with intelligent budget allocation and performance tracking.',
      icon: 'multi-channel',
    },
    {
      title: 'Real-Time Performance Analytics',
      description:
        'Live campaign monitoring and AI-driven insights that enable immediate optimization and strategy adjustments.',
      icon: 'real-time-analytics',
    },
    {
      title: 'Conversion Rate Optimization',
      description:
        'AI-powered analysis of user behavior and automated optimization to maximize conversion rates across all touchpoints.',
      icon: 'conversion-optimization',
    },
  ],

  // FAQs
  faqs: [
    {
      question: 'How does AI enhance traditional digital marketing?',
      answer:
        'AI enhances digital marketing by automating campaign optimization, predicting customer behavior, personalizing messaging at scale, and providing real-time insights that enable faster, more effective decision-making. Our AI systems analyze millions of data points to identify patterns and opportunities that traditional methods miss.',
    },
    {
      question: 'What digital marketing channels do you manage?',
      answer:
        'We manage comprehensive multi-channel campaigns including Google Ads, Facebook/Instagram Ads, LinkedIn Ads, Twitter Ads, YouTube Ads, display networks, email marketing, and content marketing. Our AI systems coordinate all channels for maximum impact and efficiency.',
    },
    {
      question: 'How do you measure marketing ROI?',
      answer:
        'We track ROI through advanced attribution modeling, customer lifetime value analysis, and conversion tracking across all touchpoints. Our AI systems provide real-time ROI predictions and automatically reallocate budget to high-performing campaigns for optimal results.',
    },
    {
      question: 'Can you help with branding and content creation?',
      answer:
        'Absolutely! Our digital marketing services include brand strategy, content creation, social media management, and creative development. We use AI to analyze audience preferences and optimize content for maximum engagement and conversion potential.',
    },
    {
      question: 'How quickly can we see results from digital marketing?',
      answer:
        'Results vary by industry and campaign type, but most clients see initial improvements within 30 days. Our AI-powered campaigns typically achieve 2-3x faster results than traditional methods, with full optimization usually complete within 90 days.',
    },
    {
      question: 'Do you offer retargeting and remarketing services?',
      answer:
        "Yes, our AI-powered retargeting services use advanced audience segmentation and predictive modeling to re-engage visitors who didn't convert initially. We create personalized remarketing campaigns that significantly improve conversion rates and customer lifetime value.",
    },
  ],

  // Service features/process
  features: [
    {
      title: 'Strategy & Audience Analysis',
      description:
        'Comprehensive analysis of your market, competitors, and target audience to develop data-driven marketing strategies.',
      features: [
        'Market research and competitive analysis',
        'Customer persona development and segmentation',
        'Channel selection and budget allocation',
        'Campaign goal setting and KPI definition',
        'AI-powered audience insights and targeting',
      ],
      forWhom: 'Marketing Teams, Business Owners',
      duration: '1-2 weeks',
      format: 'Strategy workshops and analysis reports',
    },
    {
      title: 'Campaign Setup & Optimization',
      description:
        'Professional campaign setup across all digital channels with AI-powered optimization from day one.',
      features: [
        'Multi-platform campaign configuration',
        'Creative development and A/B testing setup',
        'Audience targeting and segmentation',
        'Budget allocation and bid management',
        'Performance tracking and analytics setup',
      ],
      forWhom: 'Marketing Teams, Account Managers',
      duration: '1-3 weeks',
      format: 'Campaign launch and initial optimization',
    },
    {
      title: 'Content Creation & Management',
      description:
        'AI-enhanced content strategy and creation that resonates with your target audience and drives engagement.',
      features: [
        'Content strategy and calendar development',
        'AI-powered content creation and optimization',
        'Social media content and posting schedule',
        'Email marketing campaigns and automation',
        'Performance monitoring and content refinement',
      ],
      forWhom: 'Content Teams, Marketing Teams',
      duration: 'Ongoing',
      format: 'Weekly content delivery and monthly strategy reviews',
    },
    {
      title: 'Performance Monitoring & Scaling',
      description:
        'Continuous AI-powered campaign monitoring, optimization, and scaling for maximum ROI.',
      features: [
        'Real-time performance monitoring and alerts',
        'AI-driven campaign optimization and adjustments',
        'Budget reallocation and campaign scaling',
        'Conversion tracking and attribution analysis',
        'Monthly performance reports and strategy recommendations',
      ],
      forWhom: 'Marketing Teams, Executives',
      duration: 'Ongoing',
      format: 'Weekly optimization, monthly reporting',
    },
  ],

  // Implementation process
  implementationProcess: [
    {
      step: '1',
      title: 'Strategy & Research',
      description:
        'Comprehensive market research, audience analysis, and competitive intelligence to inform your marketing strategy.',
      icon: 'strategy',
    },
    {
      step: '2',
      title: 'Campaign Planning',
      description:
        'Detailed campaign planning with channel selection, budget allocation, and AI-powered targeting strategy development.',
      icon: 'planning',
    },
    {
      step: '3',
      title: 'Creative Development',
      description:
        'AI-enhanced creative development including ad copy, visuals, landing pages, and content optimized for performance.',
      icon: 'creative',
    },
    {
      step: '4',
      title: 'Campaign Launch',
      description:
        'Coordinated campaign launch across all selected channels with real-time monitoring and initial optimization.',
      icon: 'launch',
    },
    {
      step: '5',
      title: 'Optimization & Scaling',
      description:
        'Continuous AI-powered optimization, performance monitoring, and strategic scaling for maximum ROI.',
      icon: 'optimization',
    },
    {
      step: '6',
      title: 'Reporting & Insights',
      description:
        'Comprehensive reporting with AI-driven insights, recommendations, and strategic planning for future campaigns.',
      icon: 'reporting',
    },
  ],

  // Pricing tiers
  pricingTiers: [
    {
      name: 'Starter Marketing',
      description: 'Essential digital marketing for small businesses ready to grow online',
      price: '$2,000',
      perUnit: 'per month',
      features: [
        '2 primary marketing channels',
        'Basic audience targeting and segmentation',
        'Monthly performance reports',
        'Social media content creation (4 posts/week)',
        'Email marketing campaign management',
        'Basic conversion tracking setup',
      ],
      isPopular: false,
      ctaText: 'Start Growing',
    },
    {
      name: 'Growth Marketing',
      description: 'Comprehensive digital marketing strategy for businesses scaling rapidly',
      price: '$5,000',
      perUnit: 'per month',
      features: [
        '5 marketing channels with full management',
        'Advanced AI-powered audience targeting',
        'Automated A/B testing and optimization',
        'Professional content creation and strategy',
        'Advanced analytics and attribution modeling',
        'Weekly strategy calls and bi-weekly reports',
        'Landing page optimization and CRO',
      ],
      isPopular: true,
      ctaText: 'Most Popular',
    },
    {
      name: 'Enterprise Marketing',
      description: 'Advanced marketing automation and AI-driven campaigns for large organizations',
      price: '$10,000',
      perUnit: 'per month',
      features: [
        'Unlimited marketing channels and campaigns',
        'Enterprise-level AI and predictive analytics',
        'Custom marketing automation workflows',
        'Dedicated marketing strategist and team',
        'Advanced customer journey mapping',
        'Real-time campaign monitoring and alerts',
        'Custom reporting and executive dashboards',
        'Multi-market and international campaign management',
      ],
      isPopular: false,
      ctaText: 'Scale Globally',
    },
  ],
};
